import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AddCircleOutline,
  Casino,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  CheckCircle,
  CompareArrows,
  Bolt,
  HelpOutline,
  DocumentScanner,
  ReceiptLong,
  History,
  Search,
  Star,
  StarBorder,
  TableRows,
  ViewAgenda,
} from '@mui/icons-material';
import { alpha, type Theme } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TrackRankPill } from '../components/TrackRankPill';
import { StreakPill } from '../components/StreakPill';
import { SimDriverPanel, SimDriverPanelEmpty } from '../components/SimDriverPanel';
import { FieldCharts } from '../components/charts/FieldCharts';
import { DriverDetailsModal } from '../components/DriverDetailsModal';
import { SimMatchupModal } from '../components/SimMatchupModal';
import { BoardScanDialog } from '../components/BoardScanDialog';
import { BetSlipDialog } from '../components/BetSlipDialog';
import { syncSlip, useSlipCount } from '../utils/slipStore';
import MuiBadge from '@mui/material/Badge';
import { fmtRunTime, SimHistoryDialog } from '../components/SimHistoryDialog';
import { legKey, MAX_PARLAY_LEGS, PARLAY_FULL_MESSAGE } from '../utils/parlay';
import { LiveStrip } from '../components/LiveStrip';
import { SimDriverExplanation } from '../components/SimDriverExplanation';
import { OddsToggle } from '../components/OddsToggle';
import { Collapse, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { SegmentedToggle } from '../components/SegmentedToggle';
import { BetTab, BetToolsCard, SingleBetSelection } from '../components/BetToolsCard';
import { BookModeToggle } from '../components/BookModeToggle';
import { MomentumToggle } from '../components/MomentumToggle';
import { bookDriver } from '../utils/bookMode';
import { getBadgeColor, formatStat, statLabels, StatKey } from '../utils/statBadges';
import { getCellColor } from '../utils/cellColors';
import { positionCellSx, probCellColor, strongCellColor, tableBgFor, vivid } from '../utils/simColors';
import { track as trackUsage, useTrackChange } from '../utils/track';
import { startTraceAction } from '../utils/traceparent';
import { DriverDetails } from '../types/predictions';
import {
  getSimDriverRaces,
  getProbUncertainty,
  getTracks,
  ParlayLeg,
  ProbUncertaintyBand,
  RaceChaos,
  runSimulation,
  runSimulationMomentum,
  getTrackRankPills,
  getTrackStreaks,
  DriverStreak,
  TrackRankPill as TrackRankPillData,
  SimDriverResult,
  SimLayer,
  SimLayerBreakdown,
  SimModel,
  SimulationRun,
} from '../services/api';
import { maxScreenHeight } from '../utils/viewport';

// Simulated races per run. One fixed count: more runs only shrink noise that's already well inside the model's own
// margin of error, so it isn't a setting (the Slip Builder and parlays use more where they need it).
const SIM_RUNS = 10000;

// Race chaos, the one scenario knob next to the track (see RaceChaos in services/api.ts).
const CHAOS_OPTIONS: { value: RaceChaos; label: string; hint: string }[] = [
  { value: 'calm', label: 'Calm', hint: 'Favorites hold up more often' },
  { value: 'normal', label: 'Normal', hint: 'Our model as tested on past races' },
  { value: 'wild', label: 'Wild', hint: 'More swing and wrecks; longshots rise' },
];
const CHAOS_HELP =
  'How unpredictable the race is. Normal is our model as tested on past races. Wild adds more swing and more ' +
  'wrecks: the field bunches up, favorites drop and longshots rise. Calm keeps things more orderly, so the ' +
  'favorites hold up more often. Your call on the race, not a forecast.';
const chaosLabel = (c?: RaceChaos) => (c === 'wild' ? 'Wild race' : c === 'calm' ? 'Calm race' : 'Normal race');

// Matchup view limits (the backend accepts 2-10 drivers).
const MIN_MATCHUP_DRIVERS = 2;
const MAX_MATCHUP_DRIVERS = 10;

// Fits both "12.3%" and "+99900" so switching between probability and odds keeps the table width stable.
// Every probability column can become the bold "sorted by" column (see ProbCell's `strong`), so they're all
// sized the same, generously enough for the bold/bigger treatment whichever one currently has it.
const PROB_COL_WIDTH = 104;

// Favorited drivers, kept in this browser only (the table works without them).
const FAVORITES_KEY = 'ltb.simFavorites';
const readFavorites = (): Set<string> => {
  try {
    const raw = window.localStorage.getItem(FAVORITES_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};
const writeFavorites = (favorites: Set<string>) => {
  try {
    window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(favorites)));
  } catch {
    /* storage unavailable: keep it in memory only */
  }
};

type SortKey =
  | 'projectedRank'
  | 'driver'
  | 'winProb'
  | 'top3Prob'
  | 'top5Prob'
  | 'top10Prob'
  | 'avgFinish';

const SORT_KEY_LABELS: Record<SortKey, string> = {
  projectedRank: 'Proj',
  driver: 'Driver',
  winProb: 'Win %',
  top3Prob: 'Top 3',
  top5Prob: 'Top 5',
  top10Prob: 'Top 10',
  avgFinish: 'Avg Fin',
};

// Keys where a smaller value is better (sorted ascending by default).
const ASCENDING_KEYS: SortKey[] = ['projectedRank', 'driver', 'avgFinish'];

const formatPct = (p: number): string => {
  const pct = p * 100;
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${pct.toFixed(1)}%`;
};


/** Inline "▲ 1.2" (▲ = better), the one delta style used everywhere in the table. */
const InlineDelta: React.FC<{
  delta: number;
  invert?: boolean;
  digits?: number;
  title?: string;
  bold?: boolean;
  /** Odds "cents" can run into the thousands; abbreviate past 10,000 instead of printing every digit. */
  compact?: boolean;
  /** On a colored cell (Avg Fin): sit on a small surface-colored pill so green/red keep their contrast. */
  onFill?: boolean;
}> = ({ delta, invert, digits = 1, title, bold = true, compact, onFill }) => {
  if (Math.abs(delta) < 0.5 * 10 ** -digits) return null;
  const better = invert ? delta < 0 : delta > 0;
  const abs = Math.abs(delta);
  // The delta ("odds movement") runs into four digits far more readily than the odds value itself does, so it
  // abbreviates at a lower threshold — otherwise one big-mover row forces the whole column wider.
  const magnitude = compact && abs >= 1000 ? `${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k` : abs.toFixed(digits);
  const content = (
    <Box
      component="span"
      sx={{
        fontWeight: bold ? 700 : 400,
        color: better ? 'success.main' : 'error.main',
        whiteSpace: 'nowrap',
        // Same gray as the table rows, so the pill reads as a bit of the row showing through the colored cell.
        ...(onFill && { bgcolor: (t: Theme) => tableBgFor(t), px: 0.5, py: '1px', borderRadius: 1, lineHeight: 1.4 }),
      }}
    >
      {better ? '▲' : '▼'} {magnitude}
    </Box>
  );
  return title ? <Tooltip title={title}>{content}</Tooltip> : content;
};

/** American odds as a number (+344, -150); null at 0% or 100%. */
const americanOdds = (p: number): number | null => {
  if (p <= 0 || p >= 1) return null;
  return p < 0.5 ? ((1 - p) / p) * 100 : -(p / (1 - p)) * 100;
};

/** Line movement between two American odds, in sportsbook "cents" (crossing even money skips the -100/+100 gap). */
const oddsMovement = (a: number, b: number): number =>
  Math.sign(a) === Math.sign(b) ? Math.abs(a - b) : Math.abs(a) + Math.abs(b) - 200;

const fmtOdds = (o: number) => (o > 0 ? `+${Math.round(o)}` : `${Math.round(o)}`);

// American odds on a longshot can run into the tens of thousands (e.g. +99900 at a 0.1% win chance); abbreviate
// past ±10,000 so the column doesn't blow out for one or two extreme rows.
const fmtOddsCompact = (o: number): string => {
  const sign = o > 0 ? '+' : '-';
  const abs = Math.round(Math.abs(o));
  return abs >= 10000 ? `${sign}${Math.round(abs / 1000)}k` : `${sign}${abs}`;
};

const MIN_DELTA_ODDS_PROB = 0.001;

/** Odds-mode change vs the production model: how far the line moved (▲ = more likely). */
const OddsDeltaLine: React.FC<{ prob: number; baseProb: number; labels?: [string, string]; bold?: boolean }> = ({
  prob,
  baseProb,
  labels = ['Base model', 'with Track Specialists'],
  bold,
}) => {
  // Below 0.1% (past +99900) a line move means nothing, and Momentum's unclamped comparison can land at a tiny
  // probability whose odds are effectively infinite (e.g. "▲ 13563782407139308k" on a longshot), so no delta there.
  if (Math.min(prob, baseProb) < MIN_DELTA_ODDS_PROB) return null;
  const now = americanOdds(prob);
  const base = americanOdds(baseProb);
  if (now === null || base === null) return null;
  const moved = Math.round(oddsMovement(now, base));
  return (
    <InlineDelta
      delta={prob >= baseProb ? moved : -moved}
      digits={0}
      bold={bold}
      compact
      title={`${labels[0]} ${fmtOdds(base)} → ${fmtOdds(now)} ${labels[1]}`}
    />
  );
};

/** Plain-language size of a track-specific adjustment (logit-score units). */
const adjustmentLabel = (adj: number): string => {
  const a = Math.abs(adj);
  const size = a < 0.05 ? 'negligible' : a < 0.15 ? 'small' : a < 0.3 ? 'moderate' : 'strong';
  if (size === 'negligible') return 'no meaningful track-specific edge';
  return `${size} ${adj > 0 ? 'edge: runs better here than their form' : 'penalty: runs worse here than their form'}`;
};

// The value sits left (so its position never depends on whether this row happens to have a delta to show); the
// delta, when present, floats right. Unlike a fixed-width trailing slot, this needs no reserved space at all —
// the value's left edge is stable on its own, which is also where the header and the bar below it line up.
const NumWithDelta: React.FC<{
  text: React.ReactNode;
  delta?: React.ReactNode;
  bold?: boolean;
  textColor?: string;
  textWeight?: number;
  /** Extra left margin, so the row's hover "+" (add to parlay) at the cell's left edge never sits under the text. */
  pl?: number;
}> = ({ text, delta, bold, textColor, textWeight, pl }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', width: '100%', pl }}>
    <Box
      component="span"
      className="num-value"
      sx={{
        textAlign: 'left',
        whiteSpace: 'nowrap',
        fontSize: bold ? 16 : undefined,
        fontWeight: bold ? 800 : textWeight,
        color: textColor,
      }}
    >
      {text}
    </Box>
    {delta && (
      <Box className="num-delta" sx={{ textAlign: 'right', whiteSpace: 'nowrap', pl: 0.75, flexShrink: 0 }}>
        {delta}
      </Box>
    )}
  </Box>
);


/**
 * Probability cell (percentage or American odds) with a background bar scaled to the column maximum. With onToggleLeg,
 * hovering the row shows a + that adds the market to the parlay slip (a check once it's in the slip).
 */
const ProbCell: React.FC<{
  value: number;
  max: number;
  showOdds: boolean;
  /** This column is the active sort key: bigger bold number and a colored bar, instead of the plain gray one every column gets. */
  strong?: boolean;
  inSlip?: boolean;
  onToggleLeg?: () => void;
  /** The parlay is full: the add button is disabled, with the reason on hover. */
  legFull?: boolean;
  legName?: string;
  /** Change vs the comparison (production model, or no recent form), in probability (shown in points under the value). */
  delta?: number;
  /** Odds hover text: [comparison, this view], e.g. ['Without recent form', 'with it']. */
  deltaLabels?: [string, string];
  /** Click: check this market in the single-bet value calculator. */
  onSelect?: () => void;
  /** This market is the one in the single-bet calculator. */
  selected?: boolean;
}> = ({
  value,
  max,
  showOdds,
  strong,
  inSlip,
  onToggleLeg,
  legFull,
  legName,
  delta,
  deltaLabels,
  onSelect,
  selected,
}) => {
  const theme = useTheme();
  const width = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const rawOdds = showOdds ? americanOdds(value) : null;
  const text = showOdds ? (rawOdds === null ? 'N/A' : fmtOddsCompact(rawOdds)) : formatPct(value);
  const colWidth = PROB_COL_WIDTH;
  const cellColor = probCellColor(theme, value, strong);
  // Non-sort columns are dimmed: hovering the cell lights its number up to the sort column's color and brings its
  // delta up from dimmed to full, so any cell reads clearly while you're looking at it.
  const hoverSx = strong
    ? {}
    : {
        '& .num-value': { transition: 'color 0.15s ease' },
        '& .num-delta': { opacity: 0.45, transition: 'opacity 0.15s ease' },
        '&:hover .num-value': { color: probCellColor(theme, value, true) },
        '&:hover .num-delta': { opacity: 1 },
      };
  return (
    <TableCell
      align="left"
      sx={{
        ...hoverSx,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        position: 'relative',
        width: colWidth,
        // A bit tighter than the table's default cell padding, to leave the odds-mode delta ("▼ 221") room
        // without having to widen the whole column.
        paddingLeft: '6px !important',
        paddingRight: '6px !important',
        outline: inSlip
          ? `2px solid ${theme.palette.primary.main}`
          : selected
            ? `2px dashed ${theme.palette.primary.main}`
            : 'none',
        outlineOffset: '-2px',
        cursor: onSelect ? 'pointer' : undefined,
      }}
      onClick={
        onSelect
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onSelect();
            }
          : undefined
      }
    >
      {onToggleLeg && (
        // Hover action at the left: + adds the market to the parlay.
        <Stack spacing={0} sx={{ position: 'absolute', left: 2, top: '50%', transform: 'translateY(-50%)' }}>
          {onToggleLeg && (
        <Tooltip title={legFull ? PARLAY_FULL_MESSAGE : ''}>
        <span className={inSlip ? undefined : 'parlay-add'}>
        <IconButton
          size="small"
          disabled={legFull}
          onClick={(e) => {
            e.stopPropagation();
            onToggleLeg();
          }}
          aria-label={inSlip ? `Remove ${legName} from parlay` : `Add ${legName} to parlay`}
          sx={{
            p: 0.4,
            // Muted until the row reveals it, muted further until this specific button is hovered (not just
            // the row) — otherwise every "+" in a hovered row reads as equally actionable.
            color: inSlip ? undefined : 'text.secondary',
            '&:hover': {
              color: inSlip ? undefined : 'text.primary',
              bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.25 : 0.15),
            },
          }}
        >
          {inSlip ? <CheckCircle sx={{ fontSize: 20 }} color="primary" /> : <AddCircleOutline sx={{ fontSize: 20 }} />}
        </IconButton>
        </span>
        </Tooltip>
      )}
        </Stack>
      )}
      <Box sx={{ width: '100%', pl: onToggleLeg ? 2.75 : undefined }}>
        <NumWithDelta
          text={text}
          bold={strong}
          textColor={cellColor}
          textWeight={strong ? undefined : 600}
          delta={
            delta !== undefined ? (
              showOdds ? (
                <OddsDeltaLine prob={value} baseProb={value - delta} labels={deltaLabels} bold={strong} />
              ) : (
                <InlineDelta delta={delta * 100} digits={1} bold={strong} />
              )
            ) : undefined
          }
        />
        {/* Every column gets the bar, in the same gradient color as its number — full-strength only on the
            active sort key, so the table still reads as one ranked list but shows at a glance which stat
            you're sorted by. */}
        <Box sx={{ height: 4, borderRadius: '2px', bgcolor: 'divider', mt: 0.5, width: 72 }}>
          <Box
            sx={{
              height: 4,
              borderRadius: '2px',
              width: `${width}%`,
              bgcolor: cellColor,
            }}
          />
        </Box>
      </Box>
    </TableCell>
  );
};

/** Races behind a driver's simulation rating, grouped by driver (shared by the row stats and the details modal). */
const simDriverRacesQuery = (
  site: string,
  asOf: string,
  driver: string,
  excludedTracks: string[],
  model: SimModel = 'base'
) => ({
  queryKey: ['simDriverRaces', site, asOf, driver, excludedTracks.join('|'), model],
  queryFn: async (): Promise<Record<string, DriverDetails[]>> => {
    const rows = await getSimDriverRaces(site, asOf, [driver], excludedTracks, model);
    const grouped: Record<string, DriverDetails[]> = {};
    rows.forEach(({ Driver, ...race }) => {
      (grouped[Driver] ??= []).push(race);
    });
    return grouped;
  },
  staleTime: 30 * 60 * 1000,
});

const STAT_KEYS: StatKey[] = ['avgStart', 'avgFinish', 'avgRating', 'top15Pct', 'avgRunningPos'];

const Badge: React.FC<{ label: string; value: string; color: string; title?: string; compact?: boolean }> = ({
  label,
  value,
  color,
  title,
  compact,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: compact ? 0.25 : 0.5, flex: compact ? 1 : undefined, minWidth: 0 }} title={title}>
    <Typography variant="caption" sx={{ fontSize: compact ? '0.6rem' : '0.7rem', color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Box
      sx={{
        px: compact ? 1 : 2,
        py: 0.5,
        borderRadius: compact ? 999 : 1,
        bgcolor: color,
        width: compact ? '100%' : undefined,
        minWidth: compact ? 0 : '60px',
        textAlign: 'center',
      }}
    >
      <Typography variant="body2" sx={{ fontSize: compact ? '0.72rem' : '0.8rem', fontWeight: 'bold', color: 'black', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

/**
 * Unrolled driver row: the same stat badges as the Predictions page, averaged over the races behind the driver's
 * simulation rating (weighted by each race's share of the rating). Clicking opens the full breakdown modal.
 */
/** Races behind a driver's rating plus the weighted-average stat badges — shared by the desktop and mobile
 * unroll sections so both read the same numbers off the same fetch. */
const useDriverRaceStats = (site: string, asOf: string, driver: string, excludedTracks: string[], model: SimModel) => {
  const { data, isLoading, error } = useQuery<Record<string, DriverDetails[]>, Error>(
    simDriverRacesQuery(site, asOf, driver, excludedTracks, model)
  );
  const races = useMemo(() => data?.[driver] ?? [], [data, driver]);
  const stats = useMemo(() => {
    if (races.length === 0) return null;
    let wsum = 0;
    const acc = { avgStart: 0, avgFinish: 0, avgRating: 0, top15Pct: 0, avgRunningPos: 0 };
    races.forEach((r) => {
      const w = r.SimWeight && r.SimWeight > 0 ? r.SimWeight : 1;
      wsum += w;
      acc.avgStart += w * r.St;
      acc.avgFinish += w * r.Pos;
      acc.avgRating += w * r.DriverRating;
      // The simulator's race rows give laps-in-top-15 as a fraction (0-1); the badge expects a percentage.
      acc.top15Pct += w * (r.PctT15Lap <= 1 ? r.PctT15Lap * 100 : r.PctT15Lap);
      acc.avgRunningPos += w * r.Avg;
    });
    (Object.keys(acc) as StatKey[]).forEach((k) => (acc[k] /= wsum));
    return acc;
  }, [races]);
  return { races, stats, isLoading, error };
};

const SimExpandedStats: React.FC<{
  site: string;
  asOf: string;
  excludedTracks: string[];
  driver: string;
  breakdown?: SimLayerBreakdown;
  model: SimModel;
  onOpen: () => void;
}> = ({ site, asOf, excludedTracks, driver, breakdown, model, onOpen }) => {
  const theme = useTheme();
  // Between the row's own (lightened) background and the table header's — tucked under the row without going
  // all the way to the header's tone. Half-opacity over the ambient row color blends the two instead of
  // replacing it outright.
  const sectionBg = alpha(
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.default,
    0.5
  );
  const { races, stats, isLoading, error } = useDriverRaceStats(site, asOf, driver, excludedTracks, model);

  return (
    <Box
      onClick={onOpen}
      role="button"
      aria-label={`Open the full breakdown for ${driver}`}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        py: 1.25,
        // The cell has no padding, so the hover fills the whole expanded section; the inset lives here.
        px: 4,
        bgcolor: sectionBg,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {isLoading && <LinearProgress sx={{ my: 2 }} />}
      {error && (
        <Typography variant="body2" color="error">
          {error.message}
        </Typography>
      )}
      {stats && (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-around" flexWrap="wrap" useFlexGap sx={{ pr: 13 }}>
          {STAT_KEYS.map((k) => (
            <Badge key={k} label={statLabels[k]} value={formatStat(k, stats[k])} color={getBadgeColor(stats[k], k)} />
          ))}
          {breakdown && (
            <Badge
              label="Track adj"
              value={`${breakdown.adjustment >= 0 ? '+' : ''}${breakdown.adjustment.toFixed(2)}`}
              color={breakdown.adjustment >= 0.05 ? '#a5d6a7' : breakdown.adjustment <= -0.05 ? '#ef9a9a' : '#e0e0e0'}
              title={`Track Specialists: ${adjustmentLabel(breakdown.adjustment)}`}
            />
          )}
        </Stack>
      )}
      {stats && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}>
          {races.length} races behind the rating, weighted by each race's share
        </Typography>
      )}
      {stats && (
      <Button
        size="small"
        variant="outlined"
        startIcon={<OpenInNew fontSize="small" />}
        onClick={(e) => {
          // The whole section already opens the breakdown; stop the click from double-firing onOpen via bubbling.
          e.stopPropagation();
          onOpen();
        }}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        Explore
      </Button>
      )}
    </Box>
  );
};

/**
 * The mobile card's unrolled section: the same weighted-average stats as the desktop row (via
 * useDriverRaceStats), but laid out as a 2x2 grid of pills with a full-width "Explore" button below instead of
 * desktop's single wrapping row with the button pinned to the corner — there's no room for that here.
 */
const MobileExpandedStats: React.FC<{
  site: string;
  asOf: string;
  excludedTracks: string[];
  driver: string;
  breakdown?: SimLayerBreakdown;
  model: SimModel;
  onOpen: () => void;
}> = ({ site, asOf, excludedTracks, driver, breakdown, model, onOpen }) => {
  const { races, stats, isLoading, error } = useDriverRaceStats(site, asOf, driver, excludedTracks, model);
  return (
    <Box>
      {isLoading && <LinearProgress sx={{ my: 1 }} />}
      {error && (
        <Typography variant="body2" color="error">
          {error.message}
        </Typography>
      )}
      {stats && (
        <>
          {/* Each pill is flex:1 (not content-sized), so with a gap between them they stretch into long ovals
              filling the row, the same shape as the mockup — not small circles with big gaps. */}
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {STAT_KEYS.map((k) => (
              <Badge key={k} label={statLabels[k]} value={formatStat(k, stats[k])} color={getBadgeColor(stats[k], k)} compact />
            ))}
            {breakdown && (
              <Badge
                label="Trk adj"
                value={`${breakdown.adjustment >= 0 ? '+' : ''}${breakdown.adjustment.toFixed(2)}`}
                color={breakdown.adjustment >= 0.05 ? '#a5d6a7' : breakdown.adjustment <= -0.05 ? '#ef9a9a' : '#e0e0e0'}
                title={`Track Specialists: ${adjustmentLabel(breakdown.adjustment)}`}
                compact
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.25 }}>
            {races.length} races behind the rating
          </Typography>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={<OpenInNew fontSize="small" />}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            sx={{ mt: 1 }}
          >
            Explore
          </Button>
        </>
      )}
    </Box>
  );
};

/** One Win/Top-N tile on a mobile driver card: value, gradient color, tap-to-select, and an always-visible (not
 * hover-gated) add-to-parlay button, since touch has no hover. */
const MobileMetricTile: React.FC<{
  /** Short suffix shown right after the value on the same line, e.g. "T3", "T5", "T10", "AVG". */
  shortLabel: string;
  value: number;
  max: number;
  showOdds: boolean;
  strong?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  inSlip?: boolean;
  onToggleLeg?: () => void;
  legFull?: boolean;
  /** Change vs the comparison (Track Specialists or Momentum), in probability — same source as the table's
   * per-column deltas, just missing here before (only Win and Avg Fin showed one). */
  delta?: number;
  deltaLabels?: [string, string];
}> = ({ shortLabel, value, max, showOdds, strong, selected, onSelect, inSlip, onToggleLeg, legFull, delta, deltaLabels }) => {
  const theme = useTheme();
  const rawOdds = showOdds ? americanOdds(value) : null;
  const text = showOdds ? (rawOdds === null ? 'N/A' : fmtOddsCompact(rawOdds)) : formatPct(value);
  const color = probCellColor(theme, value, strong);
  return (
    <Box
      onClick={(e) => {
        if (!onSelect) return;
        e.stopPropagation();
        onSelect();
      }}
      sx={{
        flex: 1,
        position: 'relative',
        textAlign: 'center',
        bgcolor: 'action.hover',
        borderRadius: 1.5,
        py: 0.75,
        cursor: onSelect ? 'pointer' : undefined,
        outline: inSlip
          ? `2px solid ${theme.palette.primary.main}`
          : selected
            ? `2px dashed ${theme.palette.primary.main}`
            : 'none',
        outlineOffset: -2,
      }}
    >
      {onToggleLeg && (
        <Tooltip title={legFull ? PARLAY_FULL_MESSAGE : ''}>
          <Box component="span" sx={{ position: 'absolute', top: -6, right: -6 }}>
            <IconButton
              size="small"
              disabled={legFull}
              onClick={(e) => {
                e.stopPropagation();
                onToggleLeg();
              }}
              sx={{ p: 0.3, bgcolor: 'background.paper', border: 1, borderColor: 'divider', '&.Mui-disabled': { bgcolor: 'background.paper', opacity: 0.5 } }}
            >
              {inSlip ? <CheckCircle sx={{ fontSize: 13 }} color="primary" /> : <AddCircleOutline sx={{ fontSize: 13 }} />}
            </IconButton>
          </Box>
        </Tooltip>
      )}
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ color }}>
          {text}
        </Box>
        <Box component="span" sx={{ color: 'text.secondary', fontSize: 9, letterSpacing: '0.04em', ml: 0.25 }}>
          {shortLabel}
        </Box>
      </Typography>
      {delta !== undefined && Math.abs(delta) >= 0.0005 && (
        <Box sx={{ fontSize: 9 }}>
          <InlineDelta delta={delta * 100} digits={1} title={deltaLabels ? `${deltaLabels[0]} → ${deltaLabels[1]}` : undefined} />
        </Box>
      )}
    </Box>
  );
};

/**
 * One driver's card for the mobile layout: a self-contained card standing in for a table row — star, Win% folded
 * into the name row, Top3/Top5/Top10/Avg Fin as tap targets, and a mobile-shaped unroll (MobileExpandedStats) in
 * a Collapse instead of a second table row.
 */
// Track-rank pills ("2nd best track · driver rating"): one small query per track, cached by the backend for 6 hours.
// The results wait for them, so the table appears complete instead of changing as they arrive.
const trackRankPillsQuery = (site: string) => ({
  queryKey: ['trackRankPills', site],
  queryFn: () => getTrackRankPills(site),
  staleTime: 6 * 60 * 60 * 1000,
  retry: false,
});

// Streak pills ("T5 · 7/10" here and at the most similar tracks): the same pattern as the track-rank pills.
const trackStreaksQuery = (site: string) => ({
  queryKey: ['trackStreaks', site],
  queryFn: () => getTrackStreaks(site),
  staleTime: 6 * 60 * 60 * 1000,
  retry: false,
});

type ResultsTab = 'table' | 'tiers' | 'boom';

const MobileDriverCard: React.FC<{
  trackPill?: TrackRankPillData;
  streak?: DriverStreak;
  streakTracks?: string[];
  streakSince?: number;
  pillSince?: number;
  d: SimDriverResult;
  favorite: boolean;
  onToggleFavorite: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  showOdds: boolean;
  sortKey: SortKey;
  maxes: { win: number; top3: number; top5: number; top10: number };
  probDelta: (d: SimDriverResult, field: 'winProb' | 'top3Prob' | 'top5Prob' | 'top10Prob') => number | undefined;
  deltaLabels?: [string, string];
  legProps: (driver: string, n: number, prob: number) => {
    inSlip: boolean;
    legFull: boolean;
    onToggleLeg: () => void;
    onSelect: () => void;
    selected: boolean;
    legName: string;
  };
  compareAvgFinish?: number;
  compareProjectedRank?: number;
  site: string;
  asOf: string;
  excludedTracks: string[];
  model: SimModel;
  breakdown?: SimLayerBreakdown;
  onOpenDetails: () => void;
}> = ({
  trackPill,
  streak,
  streakTracks,
  streakSince,
  pillSince,
  d,
  favorite,
  onToggleFavorite,
  expanded,
  onToggleExpanded,
  showOdds,
  sortKey,
  maxes,
  probDelta,
  deltaLabels,
  legProps,
  compareAvgFinish,
  compareProjectedRank,
  site,
  asOf,
  excludedTracks,
  model,
  breakdown,
  onOpenDetails,
}) => {
  const theme = useTheme();
  // Whichever stat the card list is sorted by becomes the "big number" (with the progress bar) in the
  // name row; the other three of {win, top3, top5, top10} fill the small-tile row below. Defaults to Win
  // for sorts that aren't one of these four (Proj, Driver, Avg Fin).
  const metricN: Record<'winProb' | 'top3Prob' | 'top5Prob' | 'top10Prob', 1 | 3 | 5 | 10> = {
    winProb: 1,
    top3Prob: 3,
    top5Prob: 5,
    top10Prob: 10,
  };
  const metrics: {
    key: 'winProb' | 'top3Prob' | 'top5Prob' | 'top10Prob';
    shortLabel: string;
    value: number;
    max: number;
  }[] = [
    { key: 'winProb', shortLabel: 'WIN', value: d.winProb, max: maxes.win },
    { key: 'top3Prob', shortLabel: 'T3', value: d.top3Prob, max: maxes.top3 },
    { key: 'top5Prob', shortLabel: 'T5', value: d.top5Prob, max: maxes.top5 },
    { key: 'top10Prob', shortLabel: 'T10', value: d.top10Prob, max: maxes.top10 },
  ];
  const primaryKey = sortKey === 'top3Prob' || sortKey === 'top5Prob' || sortKey === 'top10Prob' ? sortKey : 'winProb';
  const primary = metrics.find((m) => m.key === primaryKey)!;
  const secondaryMetrics = metrics.filter((m) => m.key !== primaryKey);
  const primaryLeg = legProps(d.driver, metricN[primaryKey], primary.value);
  const primaryDelta = probDelta(d, primaryKey);
  const primaryText = showOdds
    ? (americanOdds(primary.value) === null ? 'N/A' : fmtOddsCompact(americanOdds(primary.value)!))
    : formatPct(primary.value);
  const primaryColor = probCellColor(theme, primary.value, true);
  const primaryPct = primary.max > 0 ? Math.min(100, (primary.value / primary.max) * 100) : 0;
  return (
    <Paper
      variant="outlined"
      onClick={onToggleExpanded}
      sx={{
        p: 1.75,
        pl: 2.5,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: favorite ? (theme.palette.mode === 'dark' ? 'rgba(98, 191, 123, 0.07)' : 'rgba(46, 125, 50, 0.05)') : undefined,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: strongCellColor(theme, vivid(getCellColor(d.projectedRank, 'position'))),
        }}
      />
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite();
          }}
          aria-label={favorite ? `Unstar ${d.driver} (also removes them from the matchup)` : `Star ${d.driver} (also adds them to the matchup)`}
          sx={{ p: 0 }}
        >
          {favorite ? <Star sx={{ fontSize: 22, color: 'warning.main' }} /> : <StarBorder sx={{ fontSize: 22, color: 'text.disabled' }} />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
            {d.driver}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }} component="div">
            Proj {d.projectedRank}
            {compareProjectedRank !== undefined && (
              <Box component="span" sx={{ ml: 0.5, fontSize: 10 }}>
                <InlineDelta delta={d.projectedRank - compareProjectedRank} invert digits={0} />
              </Box>
            )}
          </Typography>
          {(trackPill || streak) && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
              {trackPill && <TrackRankPill pill={trackPill} site={site} since={pillSince ?? 0} compact />}
              {streak && <StreakPill streak={streak} site={site} tracks={streakTracks ?? []} since={streakSince ?? 0} compact />}
            </Stack>
          )}
        </Box>
        {/* The sort-matched stat is folded into the name row (not its own tile+bar) to save a row on every
            card; still tappable to select for the single-bet calculator, with the same add-to-parlay "+"
            the table's columns have. */}
        <Box
          onClick={(e) => {
            e.stopPropagation();
            primaryLeg.onSelect();
          }}
          sx={{
            textAlign: 'right',
            cursor: 'pointer',
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            outline: primaryLeg.inSlip
              ? `2px solid ${theme.palette.primary.main}`
              : primaryLeg.selected
                ? `2px dashed ${theme.palette.primary.main}`
                : 'none',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Tooltip title={primaryLeg.legFull ? PARLAY_FULL_MESSAGE : ''}>
              <span>
                <IconButton
                  size="small"
                  disabled={primaryLeg.legFull}
                  onClick={(e) => {
                    e.stopPropagation();
                    primaryLeg.onToggleLeg();
                  }}
                  sx={{ p: 0.2, '&.Mui-disabled': { opacity: 0.4 } }}
                >
                  {primaryLeg.inSlip ? <CheckCircle sx={{ fontSize: 15 }} color="primary" /> : <AddCircleOutline sx={{ fontSize: 15, color: 'text.secondary' }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: primaryColor, lineHeight: 1 }}>{primaryText}</Typography>
          </Stack>
          {primaryDelta !== undefined && Math.abs(primaryDelta) >= 0.0005 && (
            <Box sx={{ fontSize: 11 }}>
              <InlineDelta delta={primaryDelta * 100} digits={1} title={deltaLabels ? `${deltaLabels[0]} → ${deltaLabels[1]}` : undefined} />
            </Box>
          )}
        </Box>
        {expanded ? <KeyboardArrowUp fontSize="small" sx={{ color: 'text.secondary' }} /> : <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary' }} />}
      </Stack>

      <Box sx={{ mt: 0.75, height: 4, borderRadius: 999, bgcolor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${primaryPct}%`, borderRadius: 999, bgcolor: primaryColor, transition: 'width 0.2s ease' }} />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {secondaryMetrics.map((m) => (
          <MobileMetricTile
            key={m.key}
            shortLabel={m.shortLabel}
            value={m.value}
            max={m.max}
            showOdds={showOdds}
            delta={probDelta(d, m.key)}
            deltaLabels={deltaLabels}
            {...legProps(d.driver, metricN[m.key], m.value)}
          />
        ))}
        <Box sx={{ flex: 1, textAlign: 'center', borderRadius: 1.5, py: 0.75, ...positionCellSx(d.avgFinish, theme.palette.mode) }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {d.avgFinish.toFixed(1)}
            <Box component="span" sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', ml: 0.25 }}>
              AVG
            </Box>
          </Typography>
          {compareAvgFinish !== undefined && (
            <Box sx={{ fontSize: 9 }}>
              <InlineDelta delta={d.avgFinish - compareAvgFinish} invert digits={1} onFill />
            </Box>
          )}
        </Box>
      </Stack>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }} onClick={(e) => e.stopPropagation()}>
          <MobileExpandedStats
            site={site}
            asOf={asOf}
            excludedTracks={excludedTracks}
            driver={d.driver}
            breakdown={breakdown}
            model={model}
            onOpen={onOpenDetails}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

const familyLabel = (trackType: string): string =>
  trackType === 'road' ? 'road course' : trackType === 'superspeedway' ? 'drafting track' : 'oval';

/** Sidebar panel for the two-layer model (the experimental layer, or the production model): what each layer uses. */
const LayerPanel: React.FC<{
  layer: Pick<SimLayer, 'breakdown' | 'linkedTracks' | 'configName'>;
  site: string;
  trackType: string;
  experimental?: boolean;
  /** Drops the panel's own Paper framing/padding when it's rendered inside another card (e.g. a tab). */
  embedded?: boolean;
}> = ({ layer, site, trackType, experimental = true, embedded = false }) => {
  const anyAdjustment = layer.breakdown.some((b) => Math.abs(b.adjustment) > 1e-9);
  const Frame: React.ElementType = embedded ? Box : Paper;
  return (
    <Frame sx={{ p: embedded ? 0 : 2, mb: embedded ? 2 : 0 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {experimental ? 'Track Specialists' : 'How this simulation rates drivers'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        <strong>Layer 1, form:</strong> every {familyLabel(trackType)} at equal weight (recency-weighted).{' '}
        <strong>Layer 2, adjustment:</strong> how much better or worse each driver runs at {site} and its track-specific
        matches than their form, pulled toward zero when evidence is thin.
      </Typography>
      {layer.linkedTracks.length > 0 ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Track-specific matches (beyond general driver strength)
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {layer.linkedTracks.map((t) => (
              <Stack key={t.trackKey} direction="row" justifyContent="space-between">
                <Typography variant="body2">{t.trackKey}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {t.specificR.toFixed(2)} ± {t.specificSE.toFixed(2)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No other track has a clear track-specific link to {site}; the adjustment uses {site} only.
        </Typography>
      )}
      {!anyAdjustment && (
        <Alert severity="info" sx={{ mb: 1 }}>
          No track-specific adjustments here: {familyLabel(trackType)}s rarely have enough same-family races close
          together to measure form against, so this view is family form only.
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        {experimental
          ? 'A second view alongside the Base model. See Research → How to use the Base model and Track Specialists.'
          : `Two-layer model (config ${layer.configName}). See Research → General strength vs track-specific skill.`}
      </Typography>
    </Frame>
  );
};

export const Simulator: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // A touch brighter than the toolbar/sidebar/header's shared Paper color, so the table (the main event) reads
  // as a distinct surface instead of blending into the rest of the page's chrome. Paper is already pure white
  // in light mode, so there's no headroom to brighten there — dark mode is where this actually shows.
  const tableBg = tableBgFor(theme);
  const headingBg = theme.palette.mode === 'dark' ? 'rgb(19, 21, 19)' : theme.palette.background.default;

  const [tracks, setTracks] = useState<string[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null);
  // Race chaos for the next run (the run on screen carries its own, in run.chaos).
  const [chaos, setChaos] = useState<RaceChaos>('normal');
  const nSims = SIM_RUNS;
  const [run, setRun] = useState<SimulationRun | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('winProb');
  const [sortAsc, setSortAsc] = useState(false);
  const [showAmericanOdds, setShowAmericanOdds] = useState(false);
  // Show the track-specific layer (two-layer model) instead of the production model. Both come from one run.
  const [useLayer, setUseLayer] = useState(false);
  const [bookMode, setBookMode] = useState(false);
  // Momentum view: ▲/▼ show how much the selected model's recent-form blend moves each driver.
  const [momentumOn, setMomentumOn] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null);
  // The results tabs, and the driver picked in the Tiers / Boom or bust charts (its panel replaces the bet tools).
  const [resultsTab, setResultsTab] = useState<ResultsTab>('table');
  const [panelDriver, setPanelDriver] = useState<string | null>(null);
  // Driver rows unrolled to show their stat badges.
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());
  const toggleExpanded = (driver: string) =>
    setExpandedDrivers((prev) => {
      const next = new Set(prev);
      if (next.has(driver)) next.delete(driver);
      else next.add(driver);
      return next;
    });
  // Starred drivers: pinned to the top of the table, persisted across sessions, and also the Compare/Matchup set
  // (one star means both "I care about this driver" and "put them in the matchup").
  const [favorites, setFavorites] = useState<Set<string>>(() => readFavorites());
  const toggleFavorite = (driver: string) =>
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(driver)) next.delete(driver);
      else next.add(driver);
      writeFavorites(next);
      return next;
    });
  const [driverQuery, setDriverQuery] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  // Tracks the user has removed from the sidebar (applied to the next run).
  const [pendingExcluded, setPendingExcluded] = useState<Set<string>>(new Set());
  const [matchupOpen, setMatchupOpen] = useState(false);
  // Backtest uncertainty bands, for edge confidence in the value panels.
  const { data: uncertainty } = useQuery<ProbUncertaintyBand[], Error>({
    queryKey: ['probUncertainty'],
    queryFn: getProbUncertainty,
    staleTime: 6 * 60 * 60 * 1000,
  });
  // Bet tools card: single-bet value calculator and parlay builder tabs.
  const [betTab, setBetTab] = useState<BetTab>('single');
  // Mobile: the bet-tools card surfaces as a bottom sheet instead of an inline sidebar column.
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  // Mobile: the track/sim/run controls collapse behind the compact header's "change" toggle once a run exists.
  const [mobilePickerOpen, setMobilePickerOpen] = useState(false);
  // Mobile: cards by default, with a toggle back to the same (horizontally-scrolling) table desktop uses.
  const [mobileViewMode, setMobileViewMode] = useState<'cards' | 'table'>('cards');
  // Mobile: tapping "Sorted by X" opens a menu of sort columns — the only way to change sort in card view,
  // since there are no column headers to click like the table has.
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [singleBet, setSingleBet] = useState<SingleBetSelection | null>(null);
  // Legs in the parlay slip (cleared when a different track is simulated).
  const [parlayLegs, setParlayLegs] = useState<ParlayLeg[]>([]);
  // Ignore responses for a track the user has since navigated away from.
  const activeTrack = useRef<string | null>(null);
  // Previous simulations: the saved run being shown (and when it was made). A saved run for another track is
  // held here while the track switches, since switching tracks clears the table.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [savedRunAt, setSavedRunAt] = useState<string | null>(null);
  const pendingRestore = useRef<SimulationRun | null>(null);

  useEffect(() => {
    getTracks()
      .then((t) => setTracks(t.map((x) => x.name)))
      .catch((err) => {
        console.error('Error loading tracks:', err);
        setError('Failed to load tracks');
      });
  }, []);

  useEffect(() => {
    activeTrack.current = selectedTrack;
    const restore = pendingRestore.current?.site === selectedTrack ? pendingRestore.current : null;
    pendingRestore.current = null;
    setRun(restore);
    if (!restore) setSavedRunAt(null);
    setError(null);
    setSelectedDriver(null);
    setMatchupOpen(false);
    setPendingExcluded(new Set(restore?.excludedTracks ?? []));
    setExpandedDrivers(new Set());
  }, [selectedTrack]);

  const openSavedRun = (saved: SimulationRun, createdAt: string) => {
    setSavedRunAt(createdAt);
    if (saved.site === selectedTrack) {
      setRun(saved);
      setPendingExcluded(new Set(saved.excludedTracks));
      setSelectedDriver(null);
      setExpandedDrivers(new Set());
      setError(null);
    } else {
      pendingRestore.current = saved;
      setSelectedTrack(saved.site);
    }
  };

  // Race history behind the clicked driver's rating, for the same track/date as the displayed run.
  const {
    data: driverRaces,
    isLoading: isDriverRacesLoading,
    error: driverRacesError,
  } = useQuery<Record<string, DriverDetails[]>, Error>({
    ...simDriverRacesQuery(
      run?.site ?? '',
      run?.asOf ?? '',
      selectedDriver ?? '',
      run?.excludedTracks ?? [],
      useLayer && run?.layered ? 'layered' : 'base'
    ),
    enabled: !!run && !!selectedDriver,
  });

  useEffect(() => {
    if (!isRunning) return;
    setElapsed(0);
    const start = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [isRunning]);

  const queryClient = useQueryClient();
  const handleRun = async () => {
    if (!selectedTrack) return;
    const track = selectedTrack;
    setIsRunning(true);
    setError(null);
    setSelectedDriver(null);
    setPanelDriver(null);
    setMatchupOpen(false);
    try {
      // the pills alongside the simulation, so the results appear complete (prefetchQuery never throws)
      const started = performance.now();
      startTraceAction(); // the run and its pills share one trace
      const [result] = await Promise.all([
        runSimulation(track, nSims, Array.from(pendingExcluded), true, chaos),
        queryClient.prefetchQuery(trackRankPillsQuery(track)),
        queryClient.prefetchQuery(trackStreaksQuery(track)),
      ]);
      trackUsage('action', `simulate ${track}${chaos && chaos !== 'normal' ? ` (${chaos})` : ''}`, performance.now() - started);
      if (activeTrack.current === track) {
        setRun(result);
        setSavedRunAt(null);
        setPendingExcluded(new Set(result.excludedTracks));
      }
    } catch (err) {
      if (activeTrack.current === track) {
        setError(err instanceof Error ? err.message : 'Failed to run simulation');
      }
    } finally {
      setIsRunning(false);
    }
  };

  const toggleExcluded = (trackKey: string) => {
    setPendingExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(trackKey)) {
        next.delete(trackKey);
      } else {
        next.add(trackKey);
      }
      return next;
    });
  };

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(ASCENDING_KEYS.includes(key));
    }
  };

  const layer = run?.layered;
  const layerOn = useLayer && !!layer;
  const simModel = layerOn ? 'layered' : 'base';
  // Momentum re-runs the selected model with and without recent form (its own query).
  const momentumQuery = useQuery({
    queryKey: ['simMomentum', run?.site, run?.asOf, run?.nSims, run?.excludedTracks.join('|'), simModel, run?.chaos],
    queryFn: () => runSimulationMomentum(run!.site, run!.nSims, run!.excludedTracks, simModel, run!.chaos),
    // Prefetched as soon as a run is on screen (for the model shown), so the fire button is usually instant. The
    // momentum run is a second simulation (with vs without recent form, same draws), which takes a few seconds.
    enabled: !!run,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const momentum = momentumOn ? momentumQuery.data : undefined;
  // Track-rank pills for the shown run's track; the results wait for them (or for their error: then no pills).
  const trackPills = useQuery({ ...trackRankPillsQuery(run?.site ?? ''), enabled: !!run });
  const trackStreaks = useQuery({ ...trackStreaksQuery(run?.site ?? ''), enabled: !!run });
  const pillsReady =
    !run || ((trackPills.isFetched || trackPills.isError) && (trackStreaks.isFetched || trackStreaks.isError));
  // Every driver's last races here and at similar tracks (the driver panel), and the ones with a streak pill.
  const recentByDriver = useMemo(
    () => new Map<string, DriverStreak>((trackStreaks.data?.drivers ?? []).map((s) => [s.driver, s])),
    [trackStreaks.data],
  );
  const streakByDriver = useMemo(
    () => new Map(Array.from(recentByDriver).filter(([, s]) => s.kind)),
    [recentByDriver],
  );
  const pillByDriver = useMemo(
    () => new Map((trackPills.data?.pills ?? []).map((p) => [p.driver, p])),
    [trackPills.data],
  );
  // The drivers table shows the chosen model; with Track Specialists, deltas compare it with the production model
  // from the same draws.
  const shownDrivers = useMemo(
    () => (layerOn && layer ? layer.drivers : run?.drivers ?? []),
    [layerOn, layer, run]
  );
  const baseByDriver = useMemo(() => new Map((run?.drivers ?? []).map((d) => [d.driver, d])), [run]);
  // Momentum keeps the run's numbers on screen. Its comparison is a separate simulation (with vs without recent
  // form, from the same draws), so its effect is applied to the run's numbers: the comparison row is the run's
  // value minus the form effect, and every ▲/▼ (including odds and book mode) shows exactly that effect. Not
  // clamped: the arrows stay exact even when the effect is bigger than a tiny probability.
  const noFormByDriver = useMemo(() => {
    if (!momentum || !run) return null;
    const effect = new Map(momentum.drivers.map((m) => [m.driver, m]));
    return new Map(
      shownDrivers.map((d): [string, SimDriverResult] => {
        const m = effect.get(d.driver);
        if (!m) return [d.driver, d];
        const w = m.withForm;
        const o = m.withoutForm;
        return [
          d.driver,
          {
            ...d,
            projectedRank: d.projectedRank - (w.projectedRank - o.projectedRank),
            winProb: d.winProb - (w.winProb - o.winProb),
            top3Prob: d.top3Prob - (w.top3Prob - o.top3Prob),
            top5Prob: d.top5Prob - (w.top5Prob - o.top5Prob),
            top10Prob: d.top10Prob - (w.top10Prob - o.top10Prob),
            avgFinish: d.avgFinish - (w.avgFinish - o.avgFinish),
          },
        ];
      })
    );
  }, [momentum, run, shownDrivers]);
  // Momentum's arrows (recent form) take over from Track Specialists' arrows (vs the Base model) while it's on. A
  // Calm or Wild run has no arrows of its own: it's labeled by its banner instead, and these comparisons are made
  // within it (same chaos on both sides).
  const compareByDriver = useMemo(
    () => (noFormByDriver ?? (layerOn ? baseByDriver : null)),
    [layerOn, baseByDriver, noFormByDriver]
  );
  const deltaLabels: [string, string] | undefined = noFormByDriver ? ['Without recent form', 'with it'] : undefined;
  // Book mode: the table shows our probabilities the way a sportsbook would price them (flattened, margin added).
  // It's a display lens over either model; the value calculator and parlay builder keep our own probabilities.
  const tableDrivers = useMemo(() => (bookMode ? shownDrivers.map(bookDriver) : shownDrivers), [bookMode, shownDrivers]);
  const tableBaseByDriver = useMemo(
    () => new Map(Array.from(compareByDriver?.values() ?? []).map((d) => [d.driver, bookMode ? bookDriver(d) : d])),
    [compareByDriver, bookMode]
  );
  // The production model itself may be two-layer; then its breakdown is always shown (and the switch is hidden).
  const twoLayer = run?.twoLayer;
  const showBreakdown = !!twoLayer || layerOn;
  const breakdownByDriver = useMemo(
    () => new Map((twoLayer?.breakdown ?? layer?.breakdown ?? []).map((b) => [b.driver, b])),
    [twoLayer, layer]
  );

  const sortedDrivers = useMemo(() => {
    if (!run) return [];
    const rows = [...tableDrivers];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = typeof av === 'string' ? av.localeCompare(bv as string) : (av as number) - (bv as number);
      return sortAsc ? cmp : -cmp;
    });
    return rows;
  }, [run, tableDrivers, sortKey, sortAsc]);

  const trimmedQuery = driverQuery.trim().toLowerCase();
  const visibleDrivers = useMemo(
    () =>
      sortedDrivers.filter(
        (d) =>
          (!trimmedQuery || d.driver.toLowerCase().includes(trimmedQuery)) &&
          (!favoritesOnly || favorites.has(d.driver))
      ),
    [sortedDrivers, trimmedQuery, favoritesOnly, favorites]
  );

  const maxes = useMemo(() => {
    const drivers = tableDrivers;
    const max = (f: (d: SimDriverResult) => number) => drivers.reduce((m, d) => Math.max(m, f(d)), 0);
    return {
      win: max((d) => d.winProb),
      top3: max((d) => d.top3Prob),
      top5: max((d) => d.top5Prob),
      top10: max((d) => d.top10Prob),
    };
  }, [tableDrivers]);

  const sortHeader = (
    key: SortKey,
    label: string,
    tooltip?: string,
    align: 'left' | 'right' = 'right',
    width?: number | string,
    // NumWithDelta left-aligns the value (so its position never depends on whether that row has a delta) and
    // floats the delta right past it. The header lines up with the value, so it's left-aligned here too.
    isValueColumn?: boolean,
    // Win/Top3/Top5/Top10 values get extra left margin in the body (clears the hover "+" at the cell's left
    // edge); the header needs the same margin to stay lined up with them.
    hasLegToggle?: boolean,
    // Exact left padding (px) to match a body cell that isn't using the hasLegToggle offset — the "#" column,
    // whose star button sits at 4px, not 28px.
    plPx?: number
  ) => (
    <TableCell
      align={isValueColumn ? 'left' : align}
      sx={{
        whiteSpace: 'nowrap',
        width,
        minWidth: width,
        textTransform: 'uppercase',
        fontSize: 11,
        letterSpacing: '0.06em',
        fontWeight: 700,
        color: key === sortKey ? 'primary.main' : 'text.secondary',
        paddingLeft: hasLegToggle ? '28px !important' : plPx !== undefined ? `${plPx}px !important` : undefined,
      }}
    >
      <Tooltip title={tooltip ?? ''} disableHoverListener={!tooltip}>
        <TableSortLabel
          sx={{ '& .MuiTableSortLabel-icon': { ml: 0.25, mr: 0 }, color: 'inherit', '&.Mui-active': { color: 'inherit' } }}
          active={sortKey === key}
          direction={sortKey === key ? (sortAsc ? 'asc' : 'desc') : ASCENDING_KEYS.includes(key) ? 'asc' : 'desc'}
          onClick={() => handleSort(key)}
        >
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );

  // A slip belongs to one track: clear it when a different track is simulated.
  const runSite = run?.site;
  useEffect(() => {
    setParlayLegs([]);
    setSingleBet(null);
  }, [runSite]);
  const parlayKeys = useMemo(() => new Set(parlayLegs.map(legKey)), [parlayLegs]);
  const toggleParlayLeg = (leg: ParlayLeg) => {
    if (!parlayKeys.has(legKey(leg))) setBetTab('parlay'); // show the slip when a leg is added
    setParlayLegs((prev) =>
      prev.some((l) => legKey(l) === legKey(leg))
        ? prev.filter((l) => legKey(l) !== legKey(leg))
        : prev.length >= MAX_PARLAY_LEGS
          ? prev
          : [...prev, leg]
    );
  };
  const addParlayLeg = (leg: ParlayLeg) => {
    if (!parlayKeys.has(legKey(leg))) toggleParlayLeg(leg);
  };
  const [scanOpen, setScanOpen] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);
  // Usage events (the admin page): which views and tools people use.
  useTrackChange('tab', resultsTab);
  useTrackChange('toggle', momentumOn, (v) => `momentum ${v ? 'on' : 'off'}`);
  useTrackChange('toggle', showAmericanOdds, (v) => (v ? 'odds' : 'probability'));
  useTrackChange('toggle', bookMode, (v) => `book mode ${v ? 'on' : 'off'}`);
  useTrackChange('toggle', useLayer, (v) => (v ? 'track specialists' : 'base model'));
  useTrackChange('open', panelDriver, (v) => (v ? 'driver panel' : null));
  useTrackChange('open', selectedDriver, (v) => (v ? 'driver details' : null));
  useTrackChange('open', matchupOpen, (v) => (v ? 'matchup' : null));
  useTrackChange('open', slipOpen, (v) => (v ? 'slip builder' : null));
  useTrackChange('open', scanOpen, (v) => (v ? 'board scanner' : null));
  useTrackChange('open', historyOpen, (v) => (v ? 'previous simulations' : null));
  // Bets on this track's Slip Builder slip (shown on its button; updates when bets are added from the sidebar).
  const slipCount = useSlipCount(run?.site ?? '');
  // Load this track's slip from the account (signed in), so the count and the Slip Builder match other devices.
  useEffect(() => {
    if (run?.site) void syncSlip(run.site);
  }, [run?.site]);
  const probLeg = (driver: string, n: number): ParlayLeg => ({ kind: 'top_n', driver, n });
  const probCellLegProps = (driver: string, n: number, _prob: number) => {
    const leg = probLeg(driver, n);
    return {
      inSlip: parlayKeys.has(legKey(leg)),
      // The parlay is full: adding is blocked (removing still works).
      legFull: !parlayKeys.has(legKey(leg)) && parlayLegs.length >= MAX_PARLAY_LEGS,
      onToggleLeg: () => toggleParlayLeg(leg),
      onSelect: () => {
        // Clicking the selected market again clears it.
        const isSelected = singleBet?.driver === driver && singleBet?.n === n;
        setSingleBet(isSelected ? null : { driver, n });
        setBetTab('single');
      },
      selected: singleBet?.driver === driver && singleBet?.n === n,
      legName: `${driver} ${n === 1 ? 'win' : `top ${n}`}`,
    };
  };

  const probDelta = (
    d: SimDriverResult,
    field: 'winProb' | 'top3Prob' | 'top5Prob' | 'top10Prob'
  ): number | undefined => {
    const base = tableBaseByDriver.get(d.driver);
    return base ? d[field] - base[field] : undefined;
  };

  const selectedResult = shownDrivers.find((d) => d.driver === selectedDriver) ?? null;
  const selectedBase = selectedDriver ? baseByDriver.get(selectedDriver) ?? null : null;
  const selectedCompare = selectedDriver ? compareByDriver?.get(selectedDriver) ?? null : null;
  const selectedLayered = selectedDriver ? layer?.drivers.find((d) => d.driver === selectedDriver) ?? null : null;
  const selectedBreakdown = selectedDriver ? breakdownByDriver.get(selectedDriver) ?? null : null;
  // Races for the details modal; with the layer on, tag the ones that also feed the track-specific adjustment.
  const modalRaces = useMemo(() => {
    const races = driverRaces ?? {};
    if (!showBreakdown || !selectedBreakdown) return races;
    const adjTracks = new Set(selectedBreakdown.adjTracks);
    const tagged: Record<string, DriverDetails[]> = {};
    Object.entries(races).forEach(([driver, rows]) => {
      tagged[driver] = rows.map((r) => ({ ...r, TrackAdj: !r.DNF && !!r.TrackKey && adjTracks.has(r.TrackKey) }));
    });
    return tagged;
  }, [driverRaces, showBreakdown, selectedBreakdown]);

  // Starred drivers who are in this run, in projected-finish order, so the matchup lists them consistently.
  // The star is the one selection control now — favoriting a driver also puts them in the matchup set.
  const matchupDrivers = (run?.drivers ?? [])
    .filter((d) => favorites.has(d.driver))
    .sort((a, b) => a.projectedRank - b.projectedRank)
    .map((d) => d.driver);
  const nChecked = matchupDrivers.length;
  const canMatchup = nChecked >= MIN_MATCHUP_DRIVERS && nChecked <= MAX_MATCHUP_DRIVERS;
  const matchupLabel =
    nChecked < MIN_MATCHUP_DRIVERS
      ? 'Compare'
      : nChecked > MAX_MATCHUP_DRIVERS
        ? `Compare (max ${MAX_MATCHUP_DRIVERS})`
        : `Compare (${nChecked})`;
  // Unfavorites only the drivers in this run, leaving favorites from other tracks alone.
  const clearFieldFavorites = () => {
    setFavorites((prev) => {
      const next = new Set(prev);
      matchupDrivers.forEach((d) => next.delete(d));
      writeFavorites(next);
      return next;
    });
  };

  // Track/Simulations/Run/Previous: shown inline on desktop and when no run exists yet on mobile; tucked behind
  // the compact mobile header's "change" toggle once a run exists, so the header stays a one-line summary.
  const trackPickerFormEl = (
    <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'stretch' : 'center'}>
      <FormControl sx={{ flex: 2, minWidth: 0 }}>
        <Autocomplete
          value={selectedTrack}
          onChange={(_, v) => setSelectedTrack(v)}
          options={tracks}
          disabled={isRunning}
          renderInput={(params) => <TextField {...params} label="Track" placeholder="Search tracks..." />}
        />
      </FormControl>
      <FormControl sx={{ minWidth: 170 }}>
        <InputLabel id="race-chaos-label">
          <Stack direction="row" spacing={0.5} alignItems="center" component="span">
            <span>Race chaos</span>
            <Tooltip title={CHAOS_HELP}>
              <HelpOutline sx={{ fontSize: 15, pointerEvents: 'auto' }} />
            </Tooltip>
          </Stack>
        </InputLabel>
        <Select
          labelId="race-chaos-label"
          label="Race chaos ⓘ"
          value={chaos}
          disabled={isRunning}
          onChange={(e) => setChaos(e.target.value as RaceChaos)}
          // The closed field shows just the name; the menu explains each.
          renderValue={(v) => CHAOS_OPTIONS.find((o) => o.value === v)?.label ?? 'Normal'}
        >
          {CHAOS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Box>
                <Typography variant="body2">{o.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.hint}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        size="large"
        startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <Casino />}
        disabled={!selectedTrack || isRunning}
        onClick={handleRun}
        sx={{ whiteSpace: 'nowrap', py: 1.75 }}
      >
        {isRunning ? 'Simulating…' : 'Run simulation'}
      </Button>
      <Button
        variant="outlined"
        size="large"
        startIcon={<History />}
        disabled={isRunning}
        onClick={() => setHistoryOpen(true)}
        sx={{ whiteSpace: 'nowrap', py: 1.75 }}
      >
        Previous
      </Button>
    </Stack>
  );

  // Shared between the desktop sticky sidebar and the mobile bottom sheet, so the two don't drift apart.
  const betToolsCardEl = run && (
    <BetToolsCard
      tab={betTab}
      onTabChange={setBetTab}
      drivers={shownDrivers}
      single={singleBet}
      onSingleChange={setSingleBet}
      uncertainty={uncertainty}
      site={run.site}
      excludeTracks={run.excludedTracks}
      legs={parlayLegs}
      onLegsChange={setParlayLegs}
      showOdds={showAmericanOdds}
      model={simModel}
      chaos={run.chaos}
      tracksPanel={{
        twoLayer:
          twoLayer || (layerOn && layer)
            ? {
                linkedTrackKeys: new Set((twoLayer ?? layer)!.linkedTracks.map((t) => t.trackKey)),
                family: familyLabel(run.trackType),
              }
            : undefined,
        tracks: layerOn && layer?.formTracks ? layer.formTracks : run.tracksUsed,
        appliedExcluded: run.excludedTracks,
        pendingExcluded,
        onToggle: toggleExcluded,
        onRerun: handleRun,
        onReset: () => setPendingExcluded(new Set()),
        isRunning,
      }}
      layerPanel={
        layerOn && layer ? (
          <LayerPanel embedded layer={layer} site={run.site} trackType={run.trackType} />
        ) : twoLayer ? (
          <LayerPanel embedded layer={twoLayer} site={run.site} trackType={run.trackType} experimental={false} />
        ) : undefined
      }
    />
  );

  return (
    // Breaks out of the app shell's 1200px Container (App.tsx) so this data-dense page gets real room, without
    // changing that shared layout for every other page. overflowX guards against the 100vw breakout trick
    // overflowing by a scrollbar's width on platforms where the vertical scrollbar takes up layout space.
    // 'clip', not 'hidden': per spec, pairing overflow-x:hidden with overflow-y:visible forces the visible one
    // to compute as 'auto' too, turning this box into a scroll container that confines every position:sticky
    // descendant (the sidebar) to it instead of the real viewport. 'clip' is exempt from that forced pairing.
    <Box sx={{ width: '100vw', marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)', overflowX: 'clip', overflowY: 'visible' }}>
    {isMobile && run ? (
      // Compact sticky header once a run exists: a one-line summary (track, sim count, the three actions that
      // used to be separate buttons) instead of the full desktop-shaped band shrunk to fit. The track/sim/run
      // controls aren't gone — they're behind "Change" so the header stays one line.
      <Box sx={{ position: 'sticky', top: 0, zIndex: theme.zIndex.appBar, bgcolor: headingBg, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={() => setMobilePickerOpen((v) => !v)}
              sx={{ cursor: 'pointer', minWidth: 0 }}
            >
              <Typography sx={{ fontSize: 17, fontWeight: 800 }} noWrap>
                {run.site}
              </Typography>
              <KeyboardArrowDown
                fontSize="small"
                sx={{ color: 'text.secondary', transition: 'transform 0.15s', transform: mobilePickerOpen ? 'rotate(180deg)' : undefined }}
              />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {run.chaos ? chaosLabel(run.chaos) : `${run.nSims.toLocaleString()} sims`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <IconButton onClick={() => setScanOpen(true)} aria-label="Scan a board">
                <DocumentScanner />
              </IconButton>
              <IconButton onClick={() => setSlipOpen(true)} aria-label="Slip Builder">
                <MuiBadge badgeContent={slipCount} color="primary">
                  <ReceiptLong />
                </MuiBadge>
              </IconButton>
              <IconButton onClick={() => setHistoryOpen(true)} aria-label="Previous simulations">
                <History />
              </IconButton>
            </Stack>
          </Stack>

          {/* Model / display toggles as one horizontally-scrolling pill row instead of two wrapping groups. */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, overflowX: 'auto', pb: 0.5 }}>
            {!twoLayer && (
              <>
                {resultsTab === 'table' && (
                  <MomentumToggle on={momentumOn} onChange={setMomentumOn} loading={momentumOn && momentumQuery.isFetching} />
                )}
                <Tooltip title={layer ? '' : 'Not available for this run'}>
                  <span>
                    <SegmentedToggle
                      aria-label="Model"
                      value={layerOn ? 'layered' : 'base'}
                      onChange={(v) => setUseLayer(v === 'layered')}
                      disabled={!layer}
                      options={[
                        { value: 'base', label: 'Base model' },
                        { value: 'layered', label: 'Track Specialists' },
                      ]}
                    />
                  </span>
                </Tooltip>
              </>
            )}
            {resultsTab === 'table' && (
              <>
                <OddsToggle checked={showAmericanOdds} onChange={setShowAmericanOdds} />
                <BookModeToggle on={bookMode} onChange={setBookMode} />
              </>
            )}
            {run.excludedTracks.length > 0 && (
              <Tooltip title={`Removed: ${run.excludedTracks.join(', ')}`}>
                <Chip
                  size="small"
                  label={`${run.excludedTracks.length} track${run.excludedTracks.length === 1 ? '' : 's'} removed`}
                  color="warning"
                  variant="outlined"
                  sx={{ flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Collapse in={mobilePickerOpen}>
          <Box sx={{ px: 2, pb: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}>
            {trackPickerFormEl}
          </Box>
        </Collapse>
      </Box>
    ) : (
      <Box sx={{ bgcolor: headingBg, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ maxWidth: 1360, mx: 'auto', pt: 2, pb: 3, px: isMobile ? 2 : 4 }}>
        <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 800 }}>
          Race Simulator
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Simulate 10,000 races on demand.
        </Typography>

        {trackPickerFormEl}
      </Box>
      </Box>
    )}

    {/* While a race is on: a slim strip opening the Race live page (shown under either header). */}
    <LiveStrip isMobile={isMobile} />
    {/* Saved-run banner: shown right under either header, mobile or desktop. */}
    {run && savedRunAt && !isRunning && (
      <Box sx={{ maxWidth: 1360, mx: 'auto', px: isMobile ? 2 : 4, pt: isMobile ? 1.5 : 0 }}>
        <Alert
          severity="info"
          icon={isMobile ? false : <History />}
          sx={{
            mt: isMobile ? 0 : 2,
            mb: isMobile ? 1.5 : 0,
            // Mobile gets the short, one-line form of this message (like the mockup, which only showed the time,
            // not the full explanation) — no icon and a shorter action to leave room for it.
            '& .MuiAlert-message': isMobile ? { overflow: 'hidden', minWidth: 0 } : undefined,
          }}
          action={
            <Button color="inherit" size="small" onClick={handleRun} sx={{ whiteSpace: 'nowrap' }}>
              {isMobile ? 'Re-run' : 'Re-run now'}
            </Button>
          }
        >
          {isMobile ? (
            <Typography variant="body2" noWrap>
              Saved {fmtRunTime(savedRunAt)}
            </Typography>
          ) : (
            <>
              Saved simulation from {fmtRunTime(savedRunAt)} (ratings as of {run.asOf}). Matchups, parlays and Momentum
              re-simulate with today&apos;s data.
            </>
          )}
        </Alert>
      </Box>
    )}

    {/* Race chaos banner: a Calm or Wild run is a scenario on top of the tested model, so it's labeled, and its
        arrows compare with a Normal race. */}
    {run?.chaos && !isRunning && (
      <Box sx={{ maxWidth: 1360, mx: 'auto', px: isMobile ? 2 : 4, pt: isMobile ? 0 : 0 }}>
        <Alert
          severity={run.chaos === 'wild' ? 'warning' : 'info'}
          icon={<Bolt />}
          sx={{ mt: isMobile ? 0 : 2, mb: isMobile ? 1.5 : 0 }}
        >
          <strong>{chaosLabel(run.chaos)}</strong>
          {isMobile ? ' · a scenario, not our tested model' : (
            <>
              {': '}
              {run.chaos === 'wild'
                ? 'a scenario with more swing and more wrecks than our tested model, so favorites drop and longshots rise.'
                : 'a scenario with less swing than our tested model, so the favorites hold up more often.'}
            </>
          )}
        </Alert>
      </Box>
    )}

    <Box sx={{ maxWidth: 1360, mx: 'auto', mt: 2, mb: 4, px: isMobile ? 2 : 4 }}>
      {run && !isMobile && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mt: 2 }}
        >
          {/* Left: the model (every tab). Right: Scan a board and Slip Builder. The table-only display settings live
              in the results card's second row; mobile has these controls in the sticky header's pill row instead. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ maxWidth: '100%' }}>
            {!twoLayer && (
              <Stack direction="row" alignItems="center" spacing={0.25}>
                                <Tooltip title={layer ? '' : 'Not available for this run'}>
                  <span>
                    <SegmentedToggle
                      aria-label="Model"
                      value={layerOn ? 'layered' : 'base'}
                      onChange={(v) => setUseLayer(v === 'layered')}
                      disabled={!layer}
                      options={[
                        { value: 'base', label: 'Base model' },
                        { value: 'layered', label: 'Track Specialists' },
                      ]}
                    />
                  </span>
                </Tooltip>
                <Tooltip
                  enterTouchDelay={0}
                  leaveDelay={200}
                  slotProps={{ tooltip: { sx: { maxWidth: 320, p: 1.5 } } }}
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Base model vs Track Specialists
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Base model</strong> rates drivers from this track and its most similar tracks.{' '}
                        <strong>Track Specialists</strong> uses each driver's form across every track of this kind,
                        adjusted for drivers who run better or worse here than that form predicts. Same simulated
                        races, so the ▲/▼ changes are the model, not noise.
                      </Typography>
                      <Link
                        component={RouterLink}
                        to="/research/09-using-the-base-model-and-track-specialists"
                        underline="hover"
                        sx={{ fontWeight: 600, color: 'primary.main' }}
                      >
                        Learn more: how to use both models →
                      </Link>
                    </Box>
                  }
                >
                  <IconButton size="small" aria-label="About the Base model and Track Specialists">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            {/* Only the "tracks removed" warning is shown here; it flags a run that differs from the default. */}
            {run.excludedTracks.length > 0 && (
              <Tooltip title={`Removed: ${run.excludedTracks.join(', ')}`}>
                <Chip
                  label={`${run.excludedTracks.length} track${run.excludedTracks.length === 1 ? '' : 's'} removed`}
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Stack>
          <ButtonGroup size="small" variant="outlined" sx={{ flexShrink: 0 }}>
                    <Button startIcon={<DocumentScanner fontSize="small" />} onClick={() => setScanOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                      Scan a board
                    </Button>
                    <Button startIcon={<ReceiptLong fontSize="small" />} onClick={() => setSlipOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                      Slip Builder{slipCount ? ` (${slipCount})` : ''}
                    </Button>
                  </ButtonGroup>
        </Stack>
      )}
      <SimHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} onOpenRun={openSavedRun} />

      {isRunning && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Simulating {nSims.toLocaleString()} races at {selectedTrack}… {elapsed}s (usually 5–15 seconds)
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {driverRacesError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSelectedDriver(null)}>
          {driverRacesError.message}
        </Alert>
      )}

      {!run && !isRunning && !error && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {selectedTrack
            ? <>Click <strong>Run simulation</strong> to simulate the next race at {selectedTrack}.</>
            : 'Pick a track to simulate.'}
        </Alert>
      )}

      {run && !pillsReady && !isRunning && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading the results…</Typography>
        </Stack>
      )}

      {run && pillsReady && (
        <Box sx={{ mt: 3 }}>
          {momentumOn && momentumQuery.error && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setMomentumOn(false)}>
              {(momentumQuery.error as Error).message}
            </Alert>
          )}
          {/* Phones: a flat (not bordered-card) search/favorites/sort row, closer to a native list's toolbar than
              a shrunk desktop panel. Scan a board and Slip Builder live in the sticky header; Compare lives in
              the sticky bottom bar — both reachable without scrolling back up here. */}
          {isMobile && (
            <Box sx={{ mb: 1.5 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="Search drivers…"
                  value={driverQuery}
                  onChange={(e) => setDriverQuery(e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  size="small"
                  variant={favoritesOnly ? 'contained' : 'outlined'}
                  startIcon={favoritesOnly ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                  onClick={() => setFavoritesOnly((v) => !v)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Favorites
                </Button>
                {/* All three tabs share the cards / table switch (Tiers' table view: one-line ranges; Boom or bust's:
                    outcome bars). */}
                {(resultsTab === 'table' || resultsTab === 'tiers' || resultsTab === 'boom') && (
                <Tooltip title={mobileViewMode === 'cards' ? 'Switch to table view' : 'Switch to card view'}>
                  <IconButton
                    size="small"
                    onClick={() => setMobileViewMode((v) => (v === 'cards' ? 'table' : 'cards'))}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}
                    aria-label={mobileViewMode === 'cards' ? 'Switch to table view' : 'Switch to card view'}
                  >
                    {mobileViewMode === 'cards' ? <TableRows fontSize="small" /> : <ViewAgenda fontSize="small" />}
                  </IconButton>
                </Tooltip>
                )}
              </Stack>
              {resultsTab === 'table' && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                  sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.03em', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', whiteSpace: 'nowrap' }}
                >
                  {SORT_KEY_LABELS[sortKey].toUpperCase()} {sortAsc ? '↑' : '↓'}
                </Typography>
                <Menu anchorEl={sortMenuAnchor} open={!!sortMenuAnchor} onClose={() => setSortMenuAnchor(null)}>
                  {(Object.keys(SORT_KEY_LABELS) as SortKey[]).map((key) => (
                    <MenuItem
                      key={key}
                      selected={key === sortKey}
                      onClick={() => {
                        handleSort(key);
                        setSortMenuAnchor(null);
                      }}
                    >
                      {SORT_KEY_LABELS[key]}
                      {key === sortKey ? ` ${sortAsc ? '↑' : '↓'}` : ''}
                    </MenuItem>
                  ))}
                </Menu>
                <Stack direction="row" spacing={1.75} flexWrap="wrap" rowGap={0.5}>
                  <Typography
                    variant="body2"
                    onClick={nChecked === 0 ? undefined : clearFieldFavorites}
                    sx={{ fontSize: 15, fontWeight: 700, textAlign: 'center', color: nChecked === 0 ? 'text.disabled' : 'primary.main', cursor: nChecked === 0 ? 'default' : 'pointer' }}
                  >
                    Clear favorites
                  </Typography>
                  <Typography
                    variant="body2"
                    onClick={expandedDrivers.size === 0 ? undefined : () => setExpandedDrivers(new Set())}
                    sx={{ fontSize: 15, fontWeight: 700, textAlign: 'center', color: expandedDrivers.size === 0 ? 'text.disabled' : 'primary.main', cursor: expandedDrivers.size === 0 ? 'default' : 'pointer' }}
                  >
                    Collapse all
                  </Typography>
                </Stack>
              </Stack>
              )}
            </Box>
          )}

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 360px',
              // Stretch (not "start") so the sidebar column is as tall as the table, giving the sticky
              // BetToolsCard room to travel instead of being boxed in by its own (shorter) content height.
              alignItems: 'stretch',
            }}
          >
            {/* Mobile defaults to a card per driver, closer to a native list than a shrunk table — no horizontal
                scroll, no columns to decode at a glance. The gear next to Favorites switches back to this same
                table (horizontally scrollable) for anyone who wants it. Desktop always gets the table; Matchup
                view lives in the sidebar either way. */}
            <Box sx={{ minWidth: 0 }}>
            {/* Desktop: one results card. Its header has the tabs, search, favorites and Compare (every tab: stars work
                in the charts too); a second row has the table-only controls. The sidebar starts level with it. */}
            {!isMobile && (
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderBottom: 0,
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1, pr: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={resultsTab}
                    onChange={(_, v: ResultsTab) => setResultsTab(v)}
                    sx={{ minHeight: 48, '& .MuiTab-root': { minHeight: 48, fontWeight: 600, fontSize: 13, minWidth: 0, px: 2 } }}
                  >
                    <Tab value="table" label="Table" />
                    <Tab value="tiers" label="Tiers" />
                    <Tab value="boom" label="Boom or bust" />
                  </Tabs>
                  <Box sx={{ flex: 1 }} />
                  <TextField
                  size="small"
                  placeholder="Search drivers…"
                  value={driverQuery}
                  onChange={(e) => setDriverQuery(e.target.value)}
                  sx={{ width: 200, flexShrink: 0 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                  <Button
                  size="small"
                  variant={favoritesOnly ? 'contained' : 'outlined'}
                  startIcon={favoritesOnly ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                  onClick={() => setFavoritesOnly((v) => !v)}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Favorites
                </Button>
                  <Button
                  size="small"
                  variant="contained"
                  startIcon={<CompareArrows fontSize="small" />}
                  disabled={!canMatchup}
                  onClick={() => setMatchupOpen(true)}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {matchupLabel}
                </Button>
                </Stack>
                {resultsTab === 'table' && (
                  // Left: how the table's numbers are shown. Right: the row actions.
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, pt: 1.25, pb: 0, flexWrap: 'wrap', rowGap: 1 }}>
                    <MomentumToggle
                      size="small"
                      on={momentumOn}
                      onChange={setMomentumOn}
                      loading={momentumOn && momentumQuery.isFetching}
                    />
                    <OddsToggle size="small" checked={showAmericanOdds} onChange={setShowAmericanOdds} />
                    <BookModeToggle size="small" on={bookMode} onChange={setBookMode} />
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button
                      size="small"
                      onClick={clearFieldFavorites}
                      disabled={nChecked === 0}
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Clear favorites
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setExpandedDrivers(new Set())}
                      disabled={expandedDrivers.size === 0}
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Collapse all
                    </Button>
                  </Stack>
                )}
              </Box>
            )}
            {/* Phones: the tabs on their own (the controls are in the sticky header and the toolbar above). */}
            {isMobile && (
              <Tabs
              value={resultsTab}
              onChange={(_, v: ResultsTab) => setResultsTab(v)}
              sx={{
                mb: 1.5,
                minHeight: 40,
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': { minHeight: 40, fontWeight: 600, fontSize: 13, minWidth: 0, px: 2 },
              }}
            >
              <Tab value="table" label="Table" />
              <Tab value="tiers" label="Tiers" />
              <Tab value="boom" label="Boom or bust" />
            </Tabs>
            )}
            {/* The card's body: the table or a chart, its top corners squared to meet the header. */}
            <Box
              sx={
                isMobile
                  ? undefined
                  : { '& > * > .MuiPaper-root': { borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 0 } }
              }
            >
            {resultsTab !== 'table' ? (
              <Box sx={{ pb: isMobile ? 16 : 0 }}>
                <FieldCharts
                  view={resultsTab}
                  drivers={shownDrivers}
                  visible={visibleDrivers}
                  selected={panelDriver}
                  onSelect={(d) => setPanelDriver((cur) => (cur === d ? null : d))}
                  favorites={favorites}
                  onToggleFavorite={toggleFavorite}
                  phoneTable={mobileViewMode === 'table'}
                  pills={(driver, compact) => {
                    const pill = pillByDriver.get(driver);
                    const streak = streakByDriver.get(driver);
                    if (!pill && !streak) return null;
                    return (
                      <>
                        {pill && <TrackRankPill pill={pill} site={run.site} since={trackPills.data?.since ?? 0} compact={compact} />}
                        {streak && (
                          <StreakPill
                            streak={streak}
                            site={run.site}
                            tracks={trackStreaks.data?.tracks ?? []}
                            since={trackStreaks.data?.since ?? 0}
                            compact={compact}
                          />
                        )}
                      </>
                    );
                  }}
                />
              </Box>
            ) : isMobile && mobileViewMode === 'cards' ? (
              <Stack spacing={1.5} sx={{ pb: 16 }}>
                {visibleDrivers.map((d) => (
                  <MobileDriverCard
                    key={d.driver}
                    trackPill={pillByDriver.get(d.driver)}
                    streak={streakByDriver.get(d.driver)}
                    streakTracks={trackStreaks.data?.tracks}
                    streakSince={trackStreaks.data?.since}
                    pillSince={trackPills.data?.since}
                    d={d}
                    favorite={favorites.has(d.driver)}
                    onToggleFavorite={() => toggleFavorite(d.driver)}
                    expanded={expandedDrivers.has(d.driver)}
                    onToggleExpanded={() => toggleExpanded(d.driver)}
                    showOdds={showAmericanOdds}
                    sortKey={sortKey}
                    maxes={maxes}
                    probDelta={probDelta}
                    deltaLabels={deltaLabels}
                    legProps={probCellLegProps}
                    compareAvgFinish={compareByDriver?.get(d.driver)?.avgFinish}
                    compareProjectedRank={compareByDriver?.get(d.driver)?.projectedRank}
                    site={run.site}
                    asOf={run.asOf}
                    excludedTracks={run.excludedTracks}
                    model={simModel}
                    breakdown={showBreakdown ? breakdownByDriver.get(d.driver) : undefined}
                    onOpenDetails={() => setSelectedDriver(d.driver)}
                  />
                ))}
              </Stack>
            ) : (
            <Box sx={{ minWidth: 0, pb: isMobile ? 16 : 0 }}>
            <TableContainer component={Paper} sx={{ overflowX: 'auto', bgcolor: tableBg }}>
              {/* Tighter horizontal padding so the odds view fits beside the tracks sidebar. */}
              <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1.25, py: 1.1 } }}>
                <TableHead sx={{ bgcolor: theme.palette.background.paper }}>
                  <TableRow>
                    {/* The star column doubles as "#" — Proj's old sort, since Proj no longer has its own column
                        (its rank now lives in the driver cell's subtitle, and colors the row's left rail). */}
                    {sortHeader('projectedRank', '#', 'Rank by average simulated finish', 'left', 40, false, false, 10)}
                    {/* 4px cell padding + the row's 20px expand-arrow + 8px gap before the name, so "Driver" lines
                        up with the actual driver names instead of their expand arrow. */}
                    {sortHeader('driver', 'Driver', undefined, 'left', undefined, false, false, 32)}
                    {sortHeader('winProb', 'Win', undefined, 'right', PROB_COL_WIDTH, true, true)}
                    {sortHeader('top3Prob', 'Top 3', undefined, 'right', PROB_COL_WIDTH, true, true)}
                    {sortHeader('top5Prob', 'Top 5', undefined, 'right', PROB_COL_WIDTH, true, true)}
                    {sortHeader('top10Prob', 'Top 10', undefined, 'right', PROB_COL_WIDTH, true, true)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleDrivers.map((d) => (
                    <React.Fragment key={d.driver}>
                    <TableRow
                      hover
                      onClick={() => toggleExpanded(d.driver)}
                      sx={{
                        cursor: 'pointer',
                        // Starred drivers get a faint tint so they stand out in place, wherever they sort to.
                        backgroundColor: favorites.has(d.driver)
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(98, 191, 123, 0.07)'
                            : 'rgba(46, 125, 50, 0.05)'
                          : undefined,
                        // The parlay "+" shows on row hover (always on touch devices).
                        '& .parlay-add': { opacity: 0, transition: 'opacity 0.15s, color 0.15s, background-color 0.15s' },
                        '&:hover .parlay-add': { opacity: 1 },
                        // Unstarred stars too: hidden until the row is hovered or the star has keyboard focus
                        // (always shown on touch devices, which can't hover). Starred ones always show.
                        '& .fav-star': { opacity: 0, transition: 'opacity 0.15s' },
                        '&:hover .fav-star, & .fav-star:focus-visible': { opacity: 1 },
                        '@media (hover: none)': { '& .parlay-add': { opacity: 0.6 }, '& .fav-star': { opacity: 1 } },
                      }}
                    >
                      <TableCell padding="none" sx={{ width: 40, padding: '0 2px 0 10px !important', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                        {/* Proj's old color, relocated: an inset rail rather than a full-bleed row border. */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 7,
                            bottom: 7,
                            width: 3,
                            borderRadius: '0 2px 2px 0',
                            bgcolor: strongCellColor(theme, vivid(getCellColor(d.projectedRank, 'position'))),
                          }}
                        />
                        <IconButton
                          size="small"
                          className={favorites.has(d.driver) ? undefined : 'fav-star'}
                          onClick={() => toggleFavorite(d.driver)}
                          aria-label={
                            favorites.has(d.driver)
                              ? `Unstar ${d.driver} (also removes them from the matchup)`
                              : `Star ${d.driver} (also adds them to the matchup)`
                          }
                          sx={{ p: 0, width: 24, height: 24 }}
                        >
                          {favorites.has(d.driver) ? (
                            <Star sx={{ fontSize: 20, color: 'warning.main' }} />
                          ) : (
                            <StarBorder sx={{ fontSize: 20, color: 'text.disabled' }} />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          paddingLeft: '4px !important',
                          // The race-count ? shows only while the name cell is hovered (always on touch screens).
                          '& .driver-help': { visibility: 'hidden' },
                          '&:hover .driver-help': { visibility: 'visible' },
                          '@media (hover: none)': { '& .driver-help': { visibility: 'visible' } },
                        }}
                      >
                        <Stack component="span" direction="row" alignItems="center" spacing={1}>
                          {expandedDrivers.has(d.driver) ? (
                            <KeyboardArrowUp fontSize="small" sx={{ color: 'text.secondary' }} />
                          ) : (
                            <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary' }} />
                          )}
                          <Box component="span">
                            <Box component="span" sx={{ fontWeight: 700, display: 'block' }}>{d.driver}</Box>
                            <Stack component="span" direction="row" alignItems="center" spacing={0.5} sx={{ fontSize: 11, color: 'text.secondary' }}>
                              <span>Proj {d.projectedRank}</span>
                              {compareByDriver?.get(d.driver) && (
                                <InlineDelta delta={d.projectedRank - compareByDriver.get(d.driver)!.projectedRank} invert digits={0} />
                              )}
                              {pillByDriver.get(d.driver) && (
                                <Box component="span" sx={{ pl: 0.5 }}>
                                  <TrackRankPill pill={pillByDriver.get(d.driver)!} site={run.site} since={trackPills.data?.since ?? 0} />
                                </Box>
                              )}
                              {streakByDriver.get(d.driver) && (
                                <Box component="span" sx={{ pl: 0.5 }}>
                                  <StreakPill
                                    streak={streakByDriver.get(d.driver)!}
                                    site={run.site}
                                    tracks={trackStreaks.data?.tracks ?? []}
                                    since={trackStreaks.data?.since ?? 0}
                                  />
                                </Box>
                              )}
                            </Stack>
                          </Box>
                          <Tooltip
                            title={`${d.nRacesAtTrack} race${d.nRacesAtTrack === 1 ? '' : 's'} at this track, ${d.nRaces} at tracks used`}
                            enterTouchDelay={0}
                          >
                            <HelpOutline
                              className="driver-help"
                              aria-label={`Races behind ${d.driver}'s rating`}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }}
                            />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <ProbCell value={d.winProb} max={maxes.win} showOdds={showAmericanOdds} strong={sortKey === 'winProb'} {...probCellLegProps(d.driver, 1, d.winProb)} delta={probDelta(d, 'winProb')} deltaLabels={deltaLabels} />
                      <ProbCell value={d.top3Prob} max={maxes.top3} showOdds={showAmericanOdds} strong={sortKey === 'top3Prob'} {...probCellLegProps(d.driver, 3, d.top3Prob)} delta={probDelta(d, 'top3Prob')} deltaLabels={deltaLabels} />
                      <ProbCell value={d.top5Prob} max={maxes.top5} showOdds={showAmericanOdds} strong={sortKey === 'top5Prob'} {...probCellLegProps(d.driver, 5, d.top5Prob)} delta={probDelta(d, 'top5Prob')} deltaLabels={deltaLabels} />
                      <ProbCell value={d.top10Prob} max={maxes.top10} showOdds={showAmericanOdds} strong={sortKey === 'top10Prob'} {...probCellLegProps(d.driver, 10, d.top10Prob)} delta={probDelta(d, 'top10Prob')} deltaLabels={deltaLabels} />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: '0 !important', borderBottom: expandedDrivers.has(d.driver) ? undefined : 'none' }}>
                        <Collapse in={expandedDrivers.has(d.driver)} timeout="auto" unmountOnExit>
                          <SimExpandedStats
                            site={run.site}
                            asOf={run.asOf}
                            excludedTracks={run.excludedTracks}
                            driver={d.driver}
                            breakdown={showBreakdown ? breakdownByDriver.get(d.driver) : undefined}
                            model={simModel}
                            onOpen={() => setSelectedDriver(d.driver)}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
            )}
            </Box>
            </Box>

            {!isMobile && (
            <Stack spacing={3} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  position: { md: 'sticky' },
                  top: { md: 16 },
                  // The Tracks tab can run longer than the viewport; scroll it internally instead of letting it
                  // run off the bottom of the screen while pinned.
                  maxHeight: { md: 'calc(100vh - 32px)' },
                  overflowY: { md: 'auto' },
                }}
              >
              {/* The Tiers / Boom or bust tabs' sidebar is the driver panel (empty until a driver is picked). */}
              {resultsTab === 'table' ? (
                betToolsCardEl
              ) : !panelDriver || !shownDrivers.some((x) => x.driver === panelDriver) ? (
                <SimDriverPanelEmpty view={resultsTab === 'boom' ? 'boom' : 'tiers'} />
              ) : (
                <SimDriverPanel
                  d={shownDrivers.find((x) => x.driver === panelDriver)!}
                  field={shownDrivers}
                  nSims={run.nSims}
                  site={run.site}
                  trackPill={pillByDriver.get(panelDriver)}
                  pillSince={trackPills.data?.since ?? 0}
                  recent={recentByDriver.get(panelDriver)}
                  streakTracks={trackStreaks.data?.tracks ?? []}
                  streakSince={trackStreaks.data?.since ?? 0}
                  onClose={() => setPanelDriver(null)}
                  onOpenDetails={() => setSelectedDriver(panelDriver)}
                  view={resultsTab === 'boom' ? 'boom' : 'tiers'}
                  favorite={favorites.has(panelDriver)}
                  onToggleFavorite={() => toggleFavorite(panelDriver)}
                  onSelect={setPanelDriver}
                />
              )}
              </Box>
            </Stack>
            )}
          </Box>

          {/* Mobile: the sidebar's three tabs surface as a bottom sheet instead of an inline column — Compare
              lives next to them so it's reachable without scrolling back to the toolbar. */}
          {isMobile && (
            <>
              <Box
                sx={{
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: theme.zIndex.appBar,
                  background: `linear-gradient(to top, ${theme.palette.background.default} 65%, transparent)`,
                  pt: 2.5,
                }}
              >
                {canMatchup && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<CompareArrows />}
                      onClick={() => setMatchupOpen(true)}
                      sx={{ boxShadow: 6 }}
                    >
                      {matchupLabel}
                    </Button>
                  </Box>
                )}
                <Paper square sx={{ display: 'flex', borderTop: 1, borderColor: 'divider', bgcolor: tableBg }}>
                  {(
                    [
                      { tab: 'single' as BetTab, label: 'Single bet', active: singleBet !== null },
                      {
                        tab: 'parlay' as BetTab,
                        label: parlayLegs.length > 0 ? `Parlay (${parlayLegs.length})` : 'Parlay',
                        active: parlayLegs.length > 0,
                      },
                      { tab: 'tracks' as BetTab, label: twoLayer || (layerOn && layer) ? 'Form' : 'Tracks', active: false },
                    ]
                  ).map(({ tab, label, active }) => (
                    <Box
                      key={tab}
                      onClick={() => {
                        setBetTab(tab);
                        setMobileSheetOpen(true);
                      }}
                      sx={{
                        flex: 1,
                        textAlign: 'center',
                        py: 1.25,
                        borderTop: 2,
                        borderColor: mobileSheetOpen && betTab === tab ? 'primary.main' : 'transparent',
                        // A bet actually selected (not just the sheet open on that tab) lights the tab up green,
                        // so "you have something priced" is visible without opening the sheet.
                        bgcolor: active ? 'primary.main' : undefined,
                        cursor: 'pointer',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: active ? 'primary.contrastText' : undefined }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Box>
              <Drawer
                anchor="bottom"
                open={mobileSheetOpen}
                onClose={() => setMobileSheetOpen(false)}
                PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, ...maxScreenHeight(85) } }}
              >
                <Box sx={{ p: 1.5, pb: 3 }}>{betToolsCardEl}</Box>
              </Drawer>
            </>
          )}

          {slipOpen && (
            <BetSlipDialog
              open
              onClose={() => setSlipOpen(false)}
              run={run}
              drivers={shownDrivers}
              layeredDrivers={layer?.drivers}
              model={simModel}
              uncertainty={uncertainty}
              parlayLegs={parlayLegs}
            />
          )}

          {scanOpen && (
            <BoardScanDialog
              open
              onClose={() => setScanOpen(false)}
              run={run}
              uncertainty={uncertainty}
              onAddLeg={addParlayLeg}
              parlayFull={parlayLegs.length >= MAX_PARLAY_LEGS}
            />
          )}

          {matchupOpen && canMatchup && (
            <SimMatchupModal
              open
              onClose={() => setMatchupOpen(false)}
              site={run.site}
              asOf={run.asOf}
              nSims={run.nSims}
              drivers={matchupDrivers}
              excludeTracks={run.excludedTracks}
              initialShowOdds={showAmericanOdds}
              initialBookMode={bookMode}
              model={simModel}
              chaos={run.chaos}
              trackKey={run.trackKey}
              finishOdds={shownDrivers.filter((d) => matchupDrivers.includes(d.driver))}
            />
          )}

          {isMobile && run && resultsTab !== 'table' && panelDriver && shownDrivers.some((x) => x.driver === panelDriver) && (
            <Drawer
              anchor="bottom"
              open
              onClose={() => setPanelDriver(null)}
              PaperProps={{ sx: { ...maxScreenHeight(85), borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
            >
              <SimDriverPanel
                d={shownDrivers.find((x) => x.driver === panelDriver)!}
                field={shownDrivers}
                nSims={run.nSims}
                site={run.site}
                trackPill={pillByDriver.get(panelDriver)}
                pillSince={trackPills.data?.since ?? 0}
                recent={recentByDriver.get(panelDriver)}
                streakTracks={trackStreaks.data?.tracks ?? []}
                streakSince={trackStreaks.data?.since ?? 0}
                onClose={() => setPanelDriver(null)}
                onOpenDetails={() => setSelectedDriver(panelDriver)}
                  view={resultsTab === 'boom' ? 'boom' : 'tiers'}
                  favorite={favorites.has(panelDriver)}
                  onToggleFavorite={() => toggleFavorite(panelDriver)}
                  onSelect={setPanelDriver}
              />
            </Drawer>
          )}
          <DriverDetailsModal
            open={!!selectedDriver && !driverRacesError}
            onClose={() => setSelectedDriver(null)}
            driversDetails={modalRaces}
            isLoading={isDriverRacesLoading}
            chart
            trackKey={run.trackKey}
            model={simModel}
            finishOdds={selectedResult ? [selectedResult] : []}
            field={shownDrivers}
            nSims={run.nSims}
            title={selectedDriver ? `${selectedDriver}: races behind the ${run.site} simulation` : ''}
            summary={selectedResult && (
              <>
                {layer && (
                  // Same switch as above the table (shared state), so the modal and the page always agree.
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <SegmentedToggle
                      aria-label="Model"
                      size="small"
                      value={layerOn ? 'layered' : 'base'}
                      onChange={(v) => setUseLayer(v === 'layered')}
                      options={[
                        { value: 'base', label: 'Base model' },
                        { value: 'layered', label: 'Track Specialists' },
                      ]}
                    />
                  </Stack>
                )}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    variant="outlined"
                    label={<>Win {formatPct(selectedResult.winProb)}{selectedCompare && <InlineDelta delta={(selectedResult.winProb - selectedCompare.winProb) * 100} />}</>}
                  />
                  <Chip
                    variant="outlined"
                    label={<>Top 10 {formatPct(selectedResult.top10Prob)}{selectedCompare && <InlineDelta delta={(selectedResult.top10Prob - selectedCompare.top10Prob) * 100} />}</>}
                  />
                  <Chip
                    variant="outlined"
                    label={<>Projected {selectedResult.projectedRank}{selectedCompare && <InlineDelta delta={selectedResult.projectedRank - selectedCompare.projectedRank} invert digits={0} />}</>}
                  />
                  <Chip
                    variant="outlined"
                    label={<>Avg finish {selectedResult.avgFinish.toFixed(1)}{selectedCompare && <InlineDelta delta={selectedResult.avgFinish - selectedCompare.avgFinish} invert />}</>}
                  />
                  <Chip label={`Median finish ${selectedResult.medianFinish}`} variant="outlined" />
                  <Tooltip title="10th–90th percentile simulated finish">
                    <Chip label={`Finish range ${selectedResult.p10Finish}–${selectedResult.p90Finish}`} variant="outlined" />
                  </Tooltip>
                  <Tooltip title="Share of simulations where this driver did not finish">
                    <Chip label={`DNF ${formatPct(selectedResult.dnfRate)}`} variant="outlined" />
                  </Tooltip>
                </Stack>
                {twoLayer && selectedBreakdown && (
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      How this rating is built
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Form rating {selectedBreakdown.formRating.toFixed(2)} (every {familyLabel(run.trackType)}) ·
                      track-specific adjustment{' '}
                      <strong>
                        {selectedBreakdown.adjustment >= 0 ? '+' : ''}
                        {selectedBreakdown.adjustment.toFixed(2)}
                      </strong>{' '}
                      ({adjustmentLabel(selectedBreakdown.adjustment)})
                      {selectedBreakdown.adjRaces > 0
                        ? `, from ${selectedBreakdown.adjRaces} race${selectedBreakdown.adjRaces === 1 ? '' : 's'} at ${selectedBreakdown.adjTracks.join(', ')}.`
                        : '; no races at this track or its matches to measure.'}
                    </Typography>
                  </Paper>
                )}
                {selectedBreakdown && selectedBase && selectedLayered && (
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Track Specialists
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Form rating {selectedBreakdown.formRating.toFixed(2)} · adjustment{' '}
                      <strong>
                        {selectedBreakdown.adjustment >= 0 ? '+' : ''}
                        {selectedBreakdown.adjustment.toFixed(2)}
                      </strong>{' '}
                      ({adjustmentLabel(selectedBreakdown.adjustment)})
                      {selectedBreakdown.adjRaces > 0
                        ? `, from ${selectedBreakdown.adjRaces} race${selectedBreakdown.adjRaces === 1 ? '' : 's'} at ${selectedBreakdown.adjTracks.join(', ')}.`
                        : '; no races at this track or its matches to measure.'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Base model: win {formatPct(selectedBase.winProb)}, top 10 {formatPct(selectedBase.top10Prob)},
                      projected {selectedBase.projectedRank}. With Track Specialists: win {formatPct(selectedLayered.winProb)}, top 10{' '}
                      {formatPct(selectedLayered.top10Prob)}, projected {selectedLayered.projectedRank}.
                    </Typography>
                  </Paper>
                )}
                {/* The explanation is built from the production model's races, so it always uses its numbers. */}
                {layerOn && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    The AI summary below describes the Base model.
                  </Typography>
                )}
                {selectedBase && (
                  <SimDriverExplanation
                    site={run.site}
                    asOf={run.asOf}
                    driver={selectedBase.driver}
                    excludeTracks={run.excludedTracks}
                    stats={{
                      winProb: selectedBase.winProb,
                      top5Prob: selectedBase.top5Prob,
                      top10Prob: selectedBase.top10Prob,
                      avgFinish: selectedBase.avgFinish,
                      dnfRate: selectedBase.dnfRate,
                      projectedRank: selectedBase.projectedRank,
                      fieldSize: run.drivers.length,
                    }}
                  />
                )}
              </>
            )}
            subtitle={
              showBreakdown
                ? `${twoLayer ? '' : 'Track Specialists: '}Every race behind this driver's form (every ${familyLabel(run.trackType)} at equal weight). Sim Weight is the race's share of the form rating (recency only). Races tagged "track adj" also feed the track-specific adjustment. DNF races count toward the DNF rate, not skill (running <90% of the winner's laps).`
                : "Every race that fed this driver's rating. Sim Weight is the race's share of the rating (recency × track similarity). DNF races count toward the DNF rate, not skill. DNF is calculated as running <90% of the winner's laps."
            }
          />
        </Box>
      )}
    </Box>
    </Box>
  );
};
